import { mutator } from 'satcheljs';
import { declareDiagnostics } from 'owa-diagnostics';
import { hxActorCompleted, hxChangeNotification, hxObjectLoaded } from './hxDiagnosticsActions';
import { registerHxHealthDelegate } from './hxHealth';
import { HxActorId, HxObjectType } from '@outlook/hxcore_web';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

const MAX_ROWS = 500;

export const hxDiagnosticsProperties = [
    'event',
    'type',
    'id',
    'duration',
    'to native',
    'native',
    'to js',
    'item count',
];

export interface HxDiagnostics {
    hxHealthEvents: {
        event: string;
        type: string;
        id: string;
        duration: string;
        'to native': string;
        native: string;
        'to js': string;
        'item count': string;
    }[];
}

const initialHxDiagnostics: HxDiagnostics = {
    hxHealthEvents: [],
};

function formatDuration(start: Date, end: Date) {
    const duration = end.valueOf() - start.valueOf();
    return duration > 0 ? duration.toString() : '';
}

export const hxDiagnostics = declareDiagnostics(
    'HxDiagnostics',
    initialHxDiagnostics,
    hxDiagnosticsStore => {
        if (!isHostAppFeatureEnabled('hxDiagnostics')) {
            return;
        }

        registerHxHealthDelegate({
            loadResult: healthData => hxObjectLoaded(healthData),
            actorResult: healthData => hxActorCompleted(healthData),
            dataEvent: healthData => hxChangeNotification(healthData),
        });

        mutator(hxActorCompleted, ({ healthData }) => {
            if (hxDiagnosticsStore.hxHealthEvents.length === MAX_ROWS) {
                hxDiagnosticsStore.hxHealthEvents.splice(0, 1);
            }

            hxDiagnosticsStore.hxHealthEvents.push({
                event: 'Actor',
                type: HxActorId[healthData.actorId].toString(),
                id: '',
                duration: formatDuration(healthData.timeStampStartJs, healthData.timeStampEndJs),
                'to native': formatDuration(
                    healthData.timeStampStartJs,
                    healthData.timeStampStartCpp
                ),
                native: formatDuration(healthData.timeStampStartCpp, healthData.timeStampEndCpp),
                'to js': formatDuration(healthData.timeStampEndCpp, healthData.timeStampEndJs),
                'item count': '',
            });
        });

        mutator(hxChangeNotification, ({ healthData }) => {
            if (hxDiagnosticsStore.hxHealthEvents.length === MAX_ROWS) {
                hxDiagnosticsStore.hxHealthEvents.splice(0, 1);
            }

            hxDiagnosticsStore.hxHealthEvents.push({
                event: 'Change notification',
                type: '',
                id: '',
                duration: '',
                'to native': '',
                native: '',
                'to js': formatDuration(healthData.timeStampCpp, healthData.timeStampJs),
                'item count': '',
            });
        });

        mutator(hxObjectLoaded, ({ healthData }) => {
            if (hxDiagnosticsStore.hxHealthEvents.length === MAX_ROWS) {
                hxDiagnosticsStore.hxHealthEvents.splice(0, 1);
            }

            hxDiagnosticsStore.hxHealthEvents.push({
                event: 'Load',
                type: HxObjectType[healthData.objectId.getObjectType()].toString(),
                id: healthData.objectId.getHandle().toString(16),
                duration: formatDuration(healthData.timeStampStartJs, healthData.timeStampEndJs),
                'to native': formatDuration(
                    healthData.timeStampStartJs,
                    healthData.timeStampStartCpp
                ),
                native: formatDuration(healthData.timeStampStartCpp, healthData.timeStampEndCpp),
                'to js': formatDuration(healthData.timeStampEndCpp, healthData.timeStampEndJs),
                'item count': healthData.itemCount > 0 ? healthData.itemCount.toString() : '',
            });
        });
    }
);
